// @ts-strict-ignore
import { accessTypeLabelMap } from './constants';
import type { CustomerData, CustomerRbacType, CustomerTableData } from '../types';
import { RBACAccessType } from '../types';
import type { Agent } from '~/application-review/ApplicationReviewTypes';
import type { ChildCustomer } from '~/helpers/api-queries/embeddedFinance';
import type { ChildAccesses } from '~/admin/embedded-finance/types';
import type React from 'react';
import { useContext } from 'react';
import { handleNavigation } from '~/dashboard/admin/provisioning/helpers/handleCustomerNavigation';
import { UserContext } from '~/dashboard/context';

type IsCustomerActiveProps = {
  agent: Agent;
  accessType: RBACAccessType;
  url: string;
  isCustomerPersisted: boolean;
  customerId: number;
};

/**
 * Helper function to determine if a customer item is active.
 */
const isCustomerActive = ({
  accessType,
  agent,
  customerId,
  isCustomerPersisted,
  url,
}: IsCustomerActiveProps) => {
  const { isAfefParentAgent } = agent;

  const isAccessApproved = [
    RBACAccessType.Temporary,
    RBACAccessType.Permanent,
  ].includes(accessType);

  // TODO - Temporary fix while a better AFEF RBAC solution is planned
  if (isAfefParentAgent || isAccessApproved) {
    return Boolean(url
      && isCustomerPersisted
      && customerId
      && customerId >= 0);
  }

  return false;
};

type GetTableDataProps = {
  agent: Agent;
  customers: CustomerData[];
  customerRbacData: CustomerRbacType[];
  childCustomers?: ChildCustomer[] | ChildAccesses[];
};

/**
   * Helper function which matches rbac data coming from cells and customer
   * data coming from provisioning dynamodb
   */
export const getTableData = ({
  agent,
  customers,
  customerRbacData,
  childCustomers,
}: GetTableDataProps): CustomerTableData[] => {
  const customerRbacDataBySlug = (customerRbacData || []).reduce((acc, data) => {
    acc[data.organization_name_slug] = data;
    return acc;
  }, {});
  const childAccessTypeBySlug = (childCustomers || []).reduce((acc, childCustomer) => ({
    ...acc,
    [childCustomer.organization_name_slug]: childCustomer.access_type,
  }), {});

  const populatedCustomerData = customers
    .filter(({ organization_name_slug }) => {
      if (agent.isAfefParentAgent) {
        // filter customers for the agent's customer (the parent) and the child customers the agent has access to:
        return agent.customerIdsParentAgentHasAccessTo.includes(
          customerRbacDataBySlug[organization_name_slug]?.customer_id,
        );
      }

      return true;
    })
    .sort((a, b) => a.organization_name_slug.localeCompare(b.organization_name_slug))
    .map(customer => {
      const customerRbac = customerRbacDataBySlug[customer.organization_name_slug] || {};
      const customerId = customerRbac.customer_id;
      const accessType = customerRbac.access_type
        || RBACAccessType.None;

      const isActive = isCustomerActive({
        agent,
        accessType,
        url: customer.app?.url,
        isCustomerPersisted: customer.is_customer_persisted,
        customerId,
      });

      return {
        ...customer,
        access_type: accessTypeLabelMap[accessType],
        customer_id: customerId,
        child_access_type: childAccessTypeBySlug[customer.organization_name_slug],
        is_active: isActive,
        is_controlled_child: customerRbac.is_controlled_child,
        parent_customer_id: customerRbac.parent_customer_id,
      };
    });

  if (agent.isAfefParentAgent) {
    populatedCustomerData.sort(
      (a, z) => Number(z.is_parent_organization) - Number(a.is_parent_organization),
    );
  }

  return populatedCustomerData;
};

export const useHandleRowClick = (
  isEphemeralEnvironment?: boolean, ephemeralEnvCellBaseAppUrl?: string) => {
  const {
    alloyEnvironment,
  } = useContext(UserContext);
  const currentCellKey = alloyEnvironment.replace('kube', '');

  return (
    _event: React.MouseEvent<HTMLElement>,
    rowItem: CustomerTableData,
  ) => {
    if (rowItem?.is_active) {
      handleNavigation(
        {
          currentCell: currentCellKey,
          cellHostUrl: isEphemeralEnvironment ? ephemeralEnvCellBaseAppUrl : rowItem?.app?.url,
          targetCell: rowItem?.cellKey,
          path: `/admin/customers/${rowItem.customer_id}/session`,
        },
      );
    }
  };
};
